import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar, Target, Zap, Trophy, ChevronRight, CheckCircle,
  Play, Pause, RotateCcw, Flame, TrendingUp, Clock,
  Dumbbell, Wind, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// 12-Week Program Phases
const programPhases = [
  {
    phase: 1,
    name: 'Foundation',
    weeks: [1, 2, 3, 4],
    focus: 'Build base strength and movement patterns',
    intensity: 'Low to Moderate',
    color: 'from-green-500 to-emerald-600',
    goals: [
      'Master proper form on all exercises',
      'Build aerobic base',
      'Establish training habits',
      'Improve flexibility and mobility'
    ],
    weeklyStructure: {
      speed: 2,
      strength: 2,
      agility: 1,
      flexibility: 2
    }
  },
  {
    phase: 2,
    name: 'Build',
    weeks: [5, 6, 7, 8],
    focus: 'Increase strength and power output',
    intensity: 'Moderate to High',
    color: 'from-yellow-500 to-orange-600',
    goals: [
      'Increase training volume',
      'Build explosive power',
      'Improve sport-specific skills',
      'Push physical limits'
    ],
    weeklyStructure: {
      speed: 2,
      strength: 3,
      agility: 2,
      flexibility: 1
    }
  },
  {
    phase: 3,
    name: 'Peak',
    weeks: [9, 10, 11, 12],
    focus: 'Maximize performance and maintain gains',
    intensity: 'High with strategic recovery',
    color: 'from-red-500 to-rose-600',
    goals: [
      'Peak athletic performance',
      'Sport-specific conditioning',
      'Mental preparation',
      'Competition readiness'
    ],
    weeklyStructure: {
      speed: 3,
      strength: 2,
      agility: 2,
      flexibility: 1
    }
  }
];

// Weekly workout templates for each phase
const weeklyWorkouts = {
  1: [ // Phase 1 weeks
    { day: 'Monday', type: 'Strength', focus: 'Upper Body Foundation', duration: 45 },
    { day: 'Tuesday', type: 'Speed', focus: 'Sprint Mechanics', duration: 30 },
    { day: 'Wednesday', type: 'Flexibility', focus: 'Full Body Mobility', duration: 30 },
    { day: 'Thursday', type: 'Strength', focus: 'Lower Body Foundation', duration: 45 },
    { day: 'Friday', type: 'Agility', focus: 'Basic Footwork', duration: 30 },
    { day: 'Saturday', type: 'Speed', focus: 'Acceleration Drills', duration: 30 },
    { day: 'Sunday', type: 'Rest', focus: 'Active Recovery', duration: 0 },
  ],
  2: [ // Phase 2 weeks
    { day: 'Monday', type: 'Strength', focus: 'Power Development', duration: 50 },
    { day: 'Tuesday', type: 'Speed', focus: 'Top Speed Training', duration: 35 },
    { day: 'Wednesday', type: 'Agility', focus: 'Change of Direction', duration: 40 },
    { day: 'Thursday', type: 'Strength', focus: 'Explosive Strength', duration: 50 },
    { day: 'Friday', type: 'Speed', focus: 'Sprint Intervals', duration: 35 },
    { day: 'Saturday', type: 'Strength', focus: 'Full Body Power', duration: 45 },
    { day: 'Sunday', type: 'Rest', focus: 'Recovery & Stretching', duration: 0 },
  ],
  3: [ // Phase 3 weeks
    { day: 'Monday', type: 'Speed', focus: 'Max Velocity', duration: 40 },
    { day: 'Tuesday', type: 'Agility', focus: 'Sport-Specific Drills', duration: 45 },
    { day: 'Wednesday', type: 'Strength', focus: 'Maintenance', duration: 40 },
    { day: 'Thursday', type: 'Speed', focus: 'Competition Prep', duration: 35 },
    { day: 'Friday', type: 'Agility', focus: 'Reaction Training', duration: 40 },
    { day: 'Saturday', type: 'Speed', focus: 'Peak Performance', duration: 30 },
    { day: 'Sunday', type: 'Rest', focus: 'Mental Prep & Recovery', duration: 0 },
  ]
};

const TrainingProgramPage = () => {
  const { user } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(1);
  const [programStartDate, setProgramStartDate] = useState(null);
  const [completedWorkouts, setCompletedWorkouts] = useState([]);
  const [trainingMode, setTrainingMode] = useState('build'); // 'build' or 'maintain'
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgramStatus();
  }, []);

  const fetchProgramStatus = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/training-program/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.program_start) {
        setProgramStartDate(new Date(response.data.program_start));
        setCurrentWeek(response.data.current_week || 1);
        setTrainingMode(response.data.training_mode || 'build');
        setCompletedWorkouts(response.data.completed_workouts || []);
      }
    } catch (error) {
      // No program started yet
    } finally {
      setLoading(false);
    }
  };

  const startProgram = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/training-program/start`, {
        training_mode: trainingMode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProgramStartDate(new Date());
      setCurrentWeek(1);
      setShowStartDialog(false);
      toast.success('12-Week Training System started! Let\'s build.');
    } catch (error) {
      toast.error('Failed to start program');
    }
  };

  const resetProgram = async () => {
    if (!confirm('Are you sure you want to reset your program? All progress will be lost.')) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/training-program/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProgramStartDate(null);
      setCurrentWeek(1);
      setCompletedWorkouts([]);
      toast.success('System reset. Ready to build again.');
    } catch (error) {
      toast.error('Failed to reset program');
    }
  };

  const toggleTrainingMode = async () => {
    const newMode = trainingMode === 'build' ? 'maintain' : 'build';
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/training-program/mode`, 
        { mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTrainingMode(newMode);
      toast.success(`Switched to ${newMode === 'build' ? 'Build' : 'Maintain'} Mode`);
    } catch (error) {
      toast.error('Failed to change training mode');
    }
  };

  const markWorkoutComplete = async (weekNum, dayIndex) => {
    const key = `${weekNum}-${dayIndex}`;
    if (completedWorkouts.includes(key)) return;

    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/training-program/complete-workout`, {
        week: weekNum,
        day_index: dayIndex
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedWorkouts([...completedWorkouts, key]);
      toast.success('Workout completed! 💪');
    } catch (error) {
      toast.error('Failed to mark workout complete');
    }
  };

  const getCurrentPhase = () => {
    for (const phase of programPhases) {
      if (phase.weeks.includes(currentWeek)) {
        return phase;
      }
    }
    return programPhases[0];
  };

  const getWeekWorkouts = () => {
    const phase = getCurrentPhase();
    return weeklyWorkouts[phase.phase] || weeklyWorkouts[1];
  };

  const calculateProgress = () => {
    const totalWorkouts = 12 * 6; // 12 weeks * 6 workout days
    return Math.round((completedWorkouts.length / totalWorkouts) * 100);
  };

  const phase = getCurrentPhase();
  const weekWorkouts = getWeekWorkouts();
  const progress = calculateProgress();

  // Not started yet
  if (!programStartDate && !loading) {
    return (
      <DashboardLayout title="12-Week Program">
        <div className="px-4 py-6 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-aura-primary/20 to-aura-secondary/20 border-white/10">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-aura-primary to-aura-secondary flex items-center justify-center mb-6">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight mb-4">
                12-Week Training System
              </h1>
              <p className="text-aura-text-muted max-w-md mx-auto mb-8">
                A structured periodization system to build your foundation and reach peak performance. 
                Three phases, scientifically designed for maximum athletic development.
              </p>

              {/* Phase Preview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {programPhases.map((p) => (
                  <div key={p.phase} className={`p-4 rounded-xl bg-gradient-to-br ${p.color} bg-opacity-20`}>
                    <p className="text-xs text-white/70 uppercase tracking-wider">Phase {p.phase}</p>
                    <p className="font-heading font-bold text-white text-lg">{p.name}</p>
                    <p className="text-sm text-white/80">Weeks {p.weeks[0]}-{p.weeks[p.weeks.length-1]}</p>
                  </div>
                ))}
              </div>

              <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider px-8 h-12" data-testid="start-program-btn">
                    <Play className="w-5 h-5 mr-2" />
                    Start Program
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-aura-surface border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading uppercase tracking-tight">Choose Your Mode</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <p className="text-aura-text-muted text-sm">
                      Select your training intensity based on your current season:
                    </p>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => setTrainingMode('build')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          trainingMode === 'build' 
                            ? 'border-aura-primary bg-aura-primary/10' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Flame className={`w-6 h-6 ${trainingMode === 'build' ? 'text-aura-primary' : 'text-aura-text-muted'}`} />
                          <div>
                            <p className="font-heading font-bold text-white uppercase">Build Mode</p>
                            <p className="text-xs text-aura-text-muted">Off-season / No competitions</p>
                          </div>
                        </div>
                        <p className="text-sm text-aura-text-muted mt-2">
                          Higher intensity, more volume. Focus on building strength and speed.
                        </p>
                      </button>

                      <button
                        onClick={() => setTrainingMode('maintain')}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                          trainingMode === 'maintain' 
                            ? 'border-aura-secondary bg-aura-secondary/10' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Zap className={`w-6 h-6 ${trainingMode === 'maintain' ? 'text-aura-secondary' : 'text-aura-text-muted'}`} />
                          <div>
                            <p className="font-heading font-bold text-white uppercase">Maintain Mode</p>
                            <p className="text-xs text-aura-text-muted">In-season / Active competition</p>
                          </div>
                        </div>
                        <p className="text-sm text-aura-text-muted mt-2">
                          Lighter workouts, more recovery. Focus on performance and avoiding fatigue.
                        </p>
                      </button>
                    </div>

                    <Button 
                      onClick={startProgram} 
                      className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase"
                    >
                      Begin 12-Week Journey
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="12-Week Program">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Progress Header */}
        <Card className={`bg-gradient-to-r ${phase.color} border-0`}>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <Badge className="bg-white/20 text-white border-0 mb-2">
                  Phase {phase.phase}: {phase.name}
                </Badge>
                <h1 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
                  Week {currentWeek} of 12
                </h1>
                <p className="text-white/80 mt-1">{phase.focus}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-4xl font-heading font-bold text-white">{progress}%</p>
                  <p className="text-xs text-white/70 uppercase tracking-wider">Complete</p>
                </div>
                <div className="w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="48" cy="48" r="40" stroke="rgba(255,255,255,0.2)" strokeWidth="8" fill="none" />
                    <circle 
                      cx="48" cy="48" r="40" 
                      stroke="white" strokeWidth="8" fill="none"
                      strokeDasharray={`${progress * 2.51} 251`}
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <Progress value={progress} className="h-2 bg-white/20" />
            </div>
          </CardContent>
        </Card>

        {/* Training Mode & Week Selector */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xs text-aura-text-muted uppercase tracking-wider">Training Mode:</span>
            <button
              onClick={toggleTrainingMode}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                trainingMode === 'build' 
                  ? 'border-aura-primary bg-aura-primary/10 text-aura-primary' 
                  : 'border-aura-secondary bg-aura-secondary/10 text-aura-secondary'
              } hover:opacity-80`}
              data-testid="training-mode-toggle"
            >
              {trainingMode === 'build' ? (
                <>
                  <Flame className="w-4 h-4" />
                  <span className="font-heading font-bold uppercase text-sm">Build</span>
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  <span className="font-heading font-bold uppercase text-sm">Maintain</span>
                </>
              )}
              <ChevronRight className="w-4 h-4 opacity-50" />
            </button>
            <span className="text-xs text-aura-text-subtle hidden sm:inline">
              {trainingMode === 'build' ? 'Off-season intensity' : 'In-season recovery focus'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Select value={currentWeek.toString()} onValueChange={(v) => setCurrentWeek(parseInt(v))}>
              <SelectTrigger className="w-36 bg-aura-surface border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-aura-surface border-white/10">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(w => (
                  <SelectItem key={w} value={w.toString()} className="text-white">
                    Week {w}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={resetProgram} className="border-white/20 text-aura-text-muted hover:text-white">
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>
        </div>

        {/* Phase Goals */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
              Phase {phase.phase} Goals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {phase.goals.map((goal, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-aura-primary flex-shrink-0" />
                  <span className="text-sm text-white">{goal}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Weekly Schedule */}
        <div>
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
            Week {currentWeek} Schedule
          </h2>
          <div className="grid gap-3">
            {weekWorkouts.map((workout, index) => {
              const isComplete = completedWorkouts.includes(`${currentWeek}-${index}`);
              const isRest = workout.type === 'Rest';
              
              return (
                <Card 
                  key={index} 
                  className={`border-white/10 transition-all ${
                    isComplete ? 'bg-aura-success/10 border-aura-success/30' : 
                    isRest ? 'bg-white/5' : 'bg-aura-surface hover:border-white/20'
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isRest ? 'bg-white/10' :
                          workout.type === 'Speed' ? 'bg-blue-500/20' :
                          workout.type === 'Strength' ? 'bg-red-500/20' :
                          workout.type === 'Agility' ? 'bg-yellow-500/20' :
                          'bg-green-500/20'
                        }`}>
                          {isRest ? <Sparkles className="w-5 h-5 text-aura-text-muted" /> :
                           workout.type === 'Speed' ? <Wind className="w-5 h-5 text-blue-400" /> :
                           workout.type === 'Strength' ? <Dumbbell className="w-5 h-5 text-red-400" /> :
                           <Zap className="w-5 h-5 text-yellow-400" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-aura-text-muted uppercase tracking-wider">{workout.day}</span>
                            <Badge variant="outline" className="text-xs">{workout.type}</Badge>
                          </div>
                          <p className="font-heading font-bold text-white">{workout.focus}</p>
                          {workout.duration > 0 && (
                            <p className="text-xs text-aura-text-muted flex items-center gap-1 mt-1">
                              <Clock className="w-3 h-3" /> {workout.duration} min
                            </p>
                          )}
                        </div>
                      </div>
                      {!isRest && (
                        <div className="flex items-center gap-2">
                          {isComplete ? (
                            <div className="flex items-center gap-2 text-aura-success">
                              <CheckCircle className="w-5 h-5" />
                              <span className="text-sm font-medium">Done</span>
                            </div>
                          ) : (
                            <>
                              <Link to="/exercises">
                                <Button variant="outline" size="sm" className="border-white/20 text-white">
                                  View Exercises
                                </Button>
                              </Link>
                              <Button 
                                size="sm" 
                                onClick={() => markWorkoutComplete(currentWeek, index)}
                                className="bg-aura-primary hover:bg-aura-primary-hover text-white"
                              >
                                Complete
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Phase Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {programPhases.map((p) => {
            const isActive = p.phase === phase.phase;
            const completedInPhase = completedWorkouts.filter(w => {
              const weekNum = parseInt(w.split('-')[0]);
              return p.weeks.includes(weekNum);
            }).length;
            const totalInPhase = p.weeks.length * 6;
            const phaseProgress = Math.round((completedInPhase / totalInPhase) * 100);
            
            return (
              <Card 
                key={p.phase} 
                className={`border-white/10 cursor-pointer transition-all ${
                  isActive ? `bg-gradient-to-br ${p.color}` : 'bg-aura-surface hover:border-white/20'
                }`}
                onClick={() => setCurrentWeek(p.weeks[0])}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={isActive ? "secondary" : "outline"} className={isActive ? "bg-white/20 text-white border-0" : ""}>
                      Phase {p.phase}
                    </Badge>
                    <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-aura-text-muted'}`}>
                      {phaseProgress}%
                    </span>
                  </div>
                  <p className={`font-heading font-bold text-lg ${isActive ? 'text-white' : 'text-white'}`}>
                    {p.name}
                  </p>
                  <p className={`text-xs ${isActive ? 'text-white/70' : 'text-aura-text-muted'}`}>
                    Weeks {p.weeks[0]}-{p.weeks[p.weeks.length-1]} • {p.intensity}
                  </p>
                  <Progress value={phaseProgress} className={`h-1 mt-3 ${isActive ? 'bg-white/20' : 'bg-white/10'}`} />
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainingProgramPage;
